import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Capacitor } from "@capacitor/core";
import { Badge } from "@capawesome/capacitor-badge";
import { supabase } from "./src/supabaseClient";
import apiFetch from "./src/apiFetch";
// BUILD: 2026-02-26-v9 (refactored into modules)

import { T, THEMES } from "./src/theme";
import { PeopleContext } from "./src/context/PeopleContext";
import { LS_SCREEN_KEY, LS_VIEW_KEY, _getStoredName, needsNamePrompt, getDisplayName, saveDisplayName } from "./src/utils/displayName";
import { deriveIni, notifRelTime, shrinkDataUrl } from "./src/utils/helpers";
import { toLocalYMD, gsFmtH, gsFmtRange } from "./src/utils/calendarUtils";
import { MobileShell, DesktopShell, ProfileSetupModal } from "./src/components/shell";
import { Walkthrough, WALKTHROUGH_KEY } from "./src/components/onboarding/Walkthrough.jsx";
import { trackEvent } from "./src/utils/analytics";


/** Normalize a phone string to E.164, or return null if invalid. */
function normalizePhone(raw){
  if(!raw) return null;
  const digits=raw.replace(/\D/g,"");
  if(digits.length===10) return "+1"+digits;
  if(digits.length===11&&digits[0]==="1") return "+"+digits;
  if(/^\+\d{7,15}$/.test(raw.trim())) return raw.trim();
  return null;
}

/** Minimum time (ms) between background Google Calendar re-fetches on tab focus. */
const GOOGLE_REFETCH_MS = 5 * 60 * 1000;
/** Polling interval (ms) when no push-notification watch is available. */
const GOOGLE_POLL_MS = 15 * 60 * 1000;

const SCREENS=[
  {id:"onboarding",label:"Onboarding", icon:"â—Œ",group:"entry"},
  {id:"second-user",label:"Alex joins",icon:"â—Ž",group:"entry"},
  {id:"home",      label:"Home",       icon:"â—Ž",group:"main"},
  {id:"people",    label:"People",     icon:"â—‘",group:"main"},
  {id:"schedule",  label:"Schedule",   icon:"â—",group:"main"},
  {id:"calendars", label:"Calendar",   icon:"ðŸ“…",group:"main"},
  {id:"inbox",     label:"Inbox",      icon:"â—",group:"main"},
  {id:"settings",  label:"Settings",   icon:"âŠ¡",group:"util"},
];
const DEMO=[
  {s:"onboarding",label:"Welcome & onboarding",ms:2800},
  {s:"second-user",label:"Alex accepts invite",ms:3200},
  {s:"home",      label:"Dashboard v2",        ms:2600},
  {s:"people",    label:"People directory",    ms:2400,pplType:null},
  {s:"people",    label:"Fairness tracking",   ms:2400,pplType:"fairness"},
  {s:"people",    label:"Anchor nights",       ms:2400,pplType:"anchors"},
  {s:"schedule",  label:"Scheduling flow",     ms:2400},
  {s:"calendars", label:"Calendar integrations",ms:2600},
  {s:"inbox",     label:"Inbox & notifications",ms:2400},
  {s:"settings",  label:"Settings & privacy",  ms:2400},
];

export default function OrbitPrototype({user:initialUser}){
  // Capture OAuth redirect params synchronously before any effects clean them up.
  // useState initializer runs once before effects, so this is always accurate.
  const [cameFromGoogleOAuth]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.has("google_connected")||p.has("google_error");
  });
  // If the OAuth callback returned an error, capture it to show in the UI.
  const [googleOAuthError]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.get("google_error")||null;
  });
  const [cameFromOutlookOAuth]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.has("outlook_connected")||p.has("outlook_error");
  });
  const [outlookOAuthError]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.get("outlook_error")||null;
  });
  const [cameFromYahooOAuth]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.has("yahoo_connected")||p.has("yahoo_error");
  });
  const [yahooOAuthError]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.get("yahoo_error")||null;
  });
  const [cameFromCaldavConnect]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.has("caldav_connected")||p.has("caldav_error");
  });
  const [caldavConnectError]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.get("caldav_error")||null;
  });
  const [inviteToken]=useState(()=>{
    const p=new URLSearchParams(window.location.search);
    return p.get("invite")||null;
  });
  const [screen,setScreen]=useState(()=>{
    // Native app: restore last screen so reloads/resumes feel seamless.
    // Fall back to "home" on first launch (nothing stored yet).
    if(Capacitor.isNativePlatform()){
      try{const s=localStorage.getItem(LS_SCREEN_KEY);if(s)return s;}catch{}
      return "home";
    }
    // Web: returning users (name in localStorage) skip onboarding and restore last screen.
    // Use initialUser (the prop) here — `user` state is declared after this initializer runs.
    const stored=_getStoredName(initialUser?.id);
    if(stored){
      try{return localStorage.getItem(LS_SCREEN_KEY)||"home";}catch{}
      return "home";
    }
    return "onboarding";
  });
  const [schedulePersonId,setSchedulePersonId]=useState(null);
  const [scheduleType,setScheduleType]=useState(null);
  const [scheduleMode,setScheduleMode]=useState(null);
  const [peopleType,setPeopleType]=useState(null);
  const [navResets,setNavResets]=useState({});
  const [calendarDay,setCalendarDay]=useState(null);
  const [inboxNotifId,setInboxNotifId]=useState(null);
  const [peopleLoaded,setPeopleLoaded]=useState(false);
  const [userId,setUserId]=useState(null);
  const [user,setUser]=useState(initialUser||null);
  const [people,setPeople]=useState([]);
  const [agreements,setAgreements]=useState([]);
  const [calEvents,setCalEvents]=useState([]);
  const [googleCalAvail,setGoogleCalAvail]=useState(null);
  const [googleCalStatus,setGoogleCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error'
  // Multi-account: null = not yet fetched, [] = no accounts, [{email,status,calendars,selectedCalIds},...] = accounts
  const [googleAccounts,setGoogleAccounts]=useState(null);
  const [icloudCalStatus,setIcloudCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error'
  const [icloudCalAvail,setIcloudCalAvail]=useState(null); // availability object from iCloud, same shape as googleCalAvail
  const [icloudCalendars,setIcloudCalendars]=useState(null); // list from CalDAV, lifted to survive nav
  const [outlookCalAvail,setOutlookCalAvail]=useState(null);
  const [yahooCalAvail,setYahooCalAvail]=useState(null);
  const [caldavCalAvail,setCaldavCalAvail]=useState(null);
  const [icloudSelectedCalIds,setIcloudSelectedCalIds]=useState(null); // persists across Settings remounts
  const [outlookCalStatus,setOutlookCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error' | 'needs_reauth'
  const [outlookCalendars,setOutlookCalendars]=useState(null); // list from Graph API, lifted to survive nav
  const [outlookSelectedCalIds,setOutlookSelectedCalIds]=useState(null); // persists across Settings remounts
  const [ofwCalStatus,setOfwCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error'
  const [ofwUploadedAt,setOfwUploadedAt]=useState(null); // ISO string of last file upload
  const [yahooCalStatus,setYahooCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error'
  const [yahooCalendars,setYahooCalendars]=useState(null); // list from Yahoo CalDAV, lifted to survive nav
  const [yahooSelectedCalIds,setYahooSelectedCalIds]=useState(null); // persists across Settings remounts
  const [caldavCalStatus,setCaldavCalStatus]=useState(null); // null | 'loading' | 'connected' | 'error'
  const [caldavCalendars,setCaldavCalendars]=useState(null); // list from CalDAV server, lifted to survive nav
  const [caldavSelectedCalIds,setCaldavSelectedCalIds]=useState(null); // persists across Settings remounts
  const [workSchedules,setWorkSchedules]=useState([]); // [{id,label,enabled,blocks:[{day,startH,endH},...],exceptions:[...]}]
  const [workScheduleExceptions,setWorkScheduleExceptions]=useState([]); // [{id,schedule_id,label,from_date,to_date}]
  const [connectionRequests,setConnectionRequests]=useState([]); // pending incoming connection requests
  const [pendingOutgoingIds,setPendingOutgoingIds]=useState(new Set()); // linkedUserIds of people awaiting their acceptance
  const [pendingOutgoingRequests,setPendingOutgoingRequests]=useState([]); // full pending outgoing request objects {id,to_user_id}
  const [schedulingRequests,setSchedulingRequests]=useState([]); // pending incoming scheduling proposals
  const [pendingSchedulingIds,setPendingSchedulingIds]=useState(new Set()); // to_user_ids with pending outbound scheduling proposals
  const [calendarCustomNames,setCalendarCustomNames]=useState(()=>{try{const v=localStorage.getItem("orbit_cal_names");return v?JSON.parse(v):{};}catch{return{};}});
  const [prefs,setPrefs]=useState(()=>{try{const v=localStorage.getItem("orbit_prefs");return v?{...{fairnessTracking:true,anchorNights:true,coparentCal:true},...JSON.parse(v)}:{fairnessTracking:true,anchorNights:true,coparentCal:true};}catch{return{fairnessTracking:true,anchorNights:true,coparentCal:true};}});
  const [notifSettings,setNotifSettings]=useState(()=>{try{const v=localStorage.getItem("orbit_notif_settings");return v?{...{proposals:true,confirmations:true,reminders:true,fairness:true,drift:true,anchors:true},...JSON.parse(v)}:{proposals:true,confirmations:true,reminders:true,fairness:true,drift:true,anchors:true};}catch{return{proposals:true,confirmations:true,reminders:true,fairness:true,drift:true,anchors:true};}});
  // Granular email/push notification preferences (synced to Supabase notif_prefs column)
  const NOTIF_PREFS_DEFAULTS={schedule_received:{email:true,push:true},schedule_confirmed:{email:true,push:true},schedule_declined:{email:true,push:false},counter_proposed:{email:true,push:true},fairness_drift:{email:true,push:false},birthday_reminder:{email:true,push:false},anniversary_reminder:{email:true,push:false},anchor_reminder:{email:true,push:false},new_connection:{email:true,push:true},connection_drift:{email:true,push:false},fairness_summary:{email:true,push:false}};
  const [notifPrefs,setNotifPrefs]=useState(NOTIF_PREFS_DEFAULTS);
  const [priv,setPriv]=useState(()=>{try{const v=localStorage.getItem("orbit_priv");return v?{...{partner:"avail",family:"avail",friend:"busy",coparent:"avail"},...JSON.parse(v)}:{partner:"avail",family:"avail",friend:"busy",coparent:"avail"};}catch{return{partner:"avail",family:"avail",friend:"busy",coparent:"avail"};}});
  const [hideOffline,setHideOffline]=useState(()=>{try{const v=localStorage.getItem("orbit_hide_offline");return v===null?true:v==="true";}catch{return true;}});
  const [blockNotes,setBlockNotes]=useState(()=>{try{const v=localStorage.getItem("orbit_block_notes");return v===null?true:v==="true";}catch{return true;}});
  const [privOverrides,setPrivOverrides]=useState({});
  // Profile setup modal â€” shown once per session when profile fields are incomplete
  const [showProfileModal,setShowProfileModal]=useState(false);
  const [profileModalDismissed,setProfileModalDismissed]=useState(false);
  const [showWalkthrough,setShowWalkthrough]=useState(false);

  const rowToPerson=(r)=>{
    const lastSeenAt=r.last_seen_at ?? null;
    const seen=lastSeenAt ? Math.floor((Date.now()-new Date(lastSeenAt).getTime())/86400000) : null;
    return {
      id:r.id,
      name:r.name,
      ini:r.ini,
      type:r.type,
      avail:r.avail,
      next:r.next,
      lastSeenAt,
      seen,
      tgt:r.tgt,
      tgtMode:r.tgt_mode ?? r.tgtMode ?? null,
      act:r.act,
      tags:Array.isArray(r.tags)?r.tags:[],
      anniversary:r.anniversary,
      birthday:r.birthday,
      photo:r.photo ?? null,
      avatarColor:r.avatar_color ?? null,
      linkedUserId:r.linked_user_id ?? null,
      lastActive:r._lastActive ?? null,
      fairnessNotifFreq:r.fairness_notif_freq ?? null,
    };
  };
  const personToRow=(p,uid)=>({
    user_id:uid,
    name:p.name,
    ini:p.ini ?? null,
    type:p.type,
    avail:!!p.avail,
    next:p.next ?? null,
    last_seen_at:p.lastSeenAt ?? null,
    tgt:typeof p.tgt==="number"?p.tgt:null,
    tgt_mode:p.tgtMode ?? p.tgt_mode ?? null,
    // act (fairness counter) is server-authoritative and deliberately NOT written
    // from the client — the DB blocks client act writes (security_rls_hardening.sql)
    // and the server credits/resets it (scheduling/respond.js, monthly-summary cron).
    tags:Array.isArray(p.tags)?p.tags:[],
    anniversary:p.anniversary ?? null,
    birthday:p.birthday ?? null,
    photo:p.photo ?? null,
    avatar_color:p.avatarColor ?? null,
    linked_user_id:p.linkedUserId ?? null,
    fairness_notif_freq:p.fairnessNotifFreq ?? null,
  });

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const {data:{user:freshUser}}=await supabase.auth.getUser();
        if(cancelled) return;
        // If full_name is the old hardcoded "Morgan" default, strip it from
        // local state only (no Supabase write here â€” a background updateUser
        // would race with the onboarding name-save and overwrite it).
        // The onboarding name-prompt will write the real name to Supabase.
        let resolvedUser=freshUser;
        if(freshUser?.user_metadata?.full_name==="Morgan"&&freshUser?.user_metadata?.name_explicitly_set){
          resolvedUser={...freshUser,user_metadata:{...freshUser.user_metadata,full_name:null,name_explicitly_set:false}};
        }
        // If a large photo is stored in user_metadata the JWT grows to many KB
        // and every API call with Authorization header fails with HTTP 494.
        // Silently resize (or remove) it now and persist the fix.
        // Threshold: 5 KB base64 â‰ˆ ~3.7 KB decoded â€” generous enough to flag
        // any photo stored before the resize-on-upload fix was deployed.
        const storedPhoto=resolvedUser?.user_metadata?.photo;
        if(storedPhoto&&storedPhoto.length>5000){
          try{
            const small=await shrinkDataUrl(storedPhoto);
            // small may be a resized URL or null (if decode failed);
            // either way it's smaller than the original â€” always save it.
            if(!cancelled){
              const {data:ud}=await supabase.auth.updateUser({
                data:{...(resolvedUser.user_metadata||{}),photo:small||null},
              });
              // Explicitly refresh the session so the new (smaller) JWT is
              // loaded into memory immediately; without this the old token
              // lingers until its 1-hour expiry.
              await supabase.auth.refreshSession();
              // Use the server-returned user if available, else patch locally
              const updatedMeta={...(resolvedUser.user_metadata||{}),photo:small||null};
              resolvedUser=ud?.user
                ?{...ud.user,user_metadata:{...ud.user.user_metadata,...updatedMeta}}
                :{...resolvedUser,user_metadata:updatedMeta};
            }
          }catch(_){}
        }
        const uid=resolvedUser?.id||null;
        setUserId(uid);
        // Read user_profiles to backfill birthday / photo / legal_full_name that
        // may have been saved there but are absent from the JWT user_metadata.
        if(uid&&resolvedUser){
          const {data:profRow}=await supabase.from("user_profiles").select("birthday,photo,display_name,phone,notif_prefs,read_notif_ids").eq("user_id",uid).single();
          if(profRow&&!cancelled){
            const meta=resolvedUser.user_metadata||{};
            const merged={...meta};
            if(!merged.birthday&&profRow.birthday) merged.birthday=profRow.birthday;
            if(!merged.photo&&profRow.photo) merged.photo=profRow.photo;
            if(!merged.phone&&profRow.phone) merged.phone=profRow.phone;
            resolvedUser={...resolvedUser,user_metadata:merged};
            if(profRow.notif_prefs) setNotifPrefs(np=>({...np,...profRow.notif_prefs}));
            // Merge read notification IDs from Supabase with localStorage
            if(Array.isArray(profRow.read_notif_ids)&&profRow.read_notif_ids.length>0){
              profRow.read_notif_ids.forEach(id=>readIdsRef.current.add(id));
              try{localStorage.setItem("orbit_read_notif_ids",JSON.stringify([...readIdsRef.current]));}catch{}
            }
          }
        }
        if(!cancelled&&resolvedUser) setUser(resolvedUser);
        // Upsert searchable profile so other users can find this account and
        // so profile info (birthday, photo) is carried over when they're added
        if(uid&&resolvedUser?.email){
          const meta=resolvedUser.user_metadata||{};
          const profileData={
            user_id:uid,
            display_name:meta.full_name||null,
            email:resolvedUser.email,
            phone:normalizePhone(meta.phone)||null,
            updated_at:new Date().toISOString(),
          };
          // Only sync birthday/photo when explicitly present in user_metadata;
          // otherwise leave whatever the user saved in user_profiles intact.
          if('birthday' in meta) profileData.birthday=meta.birthday||null;
          if('photo' in meta) profileData.photo=meta.photo||null;
          supabase.from("user_profiles").upsert(profileData,{onConflict:"user_id"}).then(()=>{});
        }
        if(!uid){
          setPeople([]);
          setPeopleLoaded(true);
          return;
        }
        const {data, error}=await supabase.from("people").select("*").eq("user_id",uid).order("created_at",{ascending:true});
        if(error) throw error;
        if((data||[]).length===0){
          setPeople([]);
        }else{
          // Sync name/birthday/photo from linked Orbit users' live profiles
          let rows=data;
          const linkedIds=rows.filter(r=>r.linked_user_id).map(r=>r.linked_user_id);
          if(linkedIds.length>0){
            // Reads connected users' profiles via the connected_profiles view,
            // which exposes only safe columns (no email/phone/legal name). The
            // base user_profiles table is owner-only SELECT (see
            // security_rls_hardening.sql §5).
            const {data:profiles}=await supabase.from("connected_profiles").select("user_id,display_name,birthday,photo,updated_at").in("user_id",linkedIds);
            if(profiles&&profiles.length>0){
              const pm={};
              profiles.forEach(p=>{pm[p.user_id]=p;});
              rows=rows.map(r=>{
                const prof=pm[r.linked_user_id];
                if(!prof) return r;
                return {...r,name:prof.display_name||r.name,birthday:prof.birthday??r.birthday,photo:prof.photo??r.photo,_lastActive:prof.updated_at??null};
              });
            }
          }
          setPeople(rows.map(rowToPerson));
        }
      }catch(e){
        setPeople([]);
      }finally{
        if(!cancelled) setPeopleLoaded(true);
      }
    })();
    return()=>{cancelled=true;};
  },[]);

  // Tracks scheduling_request IDs already credited to p.act so fetchSentSchedulingResponses
  // doesn't double-count fairness on repeated polling.
  const fairnessAppliedRef=useRef(new Set());

  // Tracks external calendar event IDs already credited to p.act via the month-scan below.
  // Lazily initialized from localStorage (keyed by userId) so the same event is never
  // counted twice across page reloads or re-fetches.

  // Timestamp of the last successful Google Calendar fetch â€” used to decide
  // whether a visibility-change event should trigger a re-fetch.
  const googleLastFetchRef=useRef(0);

  // Core function that fetches Google Calendar events and updates state.
  // Returns true on success so callers can chain follow-up work.
  // from/to are optional YYYY-MM-DD strings; if omitted, API defaults to current week.
  const fetchGoogleCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/google/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setGoogleCalStatus(null);return false;}
      if(res.status===401){
        const body=await res.json().catch(()=>({}));
        setGoogleCalStatus(body.error==="needs_reauth"?"needs_reauth":"error");
        return false;
      }
      if(!res.ok){setGoogleCalStatus("error");return false;}
      const json=await res.json();
      setGoogleCalAvail(json.availability);
      setGoogleCalStatus("connected");
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="google"),...(json.events||[])]);
      googleLastFetchRef.current=Date.now();
      return true;
    }catch{
      setGoogleCalStatus("error");
      return false;
    }
  },[setGoogleCalAvail,setGoogleCalStatus,setCalEvents]);

  // Core function that fetches iCloud Calendar events and updates state.
  // from/to are optional YYYY-MM-DD strings; if omitted, API defaults to current week.
  const fetchICloudCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/icloud/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setIcloudCalStatus(null);return false;}
      if(!res.ok){setIcloudCalStatus("error");return false;}
      const json=await res.json();
      setIcloudCalStatus("connected");
      setIcloudCalAvail(json.availability);
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="icloud"),...(json.events||[])]);
      return true;
    }catch{
      setIcloudCalStatus(prev=>prev==="connected"?"error":null);
      return false;
    }
  },[setIcloudCalStatus,setIcloudCalAvail,setCalEvents]);

  // Core function that fetches Outlook Calendar events and updates state.
  // from/to are optional YYYY-MM-DD strings; if omitted, API defaults to current week.
  const fetchOutlookCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/outlook/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setOutlookCalStatus(null);return false;}
      if(!res.ok){
        const body=await res.json().catch(()=>({}));
        setOutlookCalStatus(body.error==="needs_reauth"?"needs_reauth":"error");
        return false;
      }
      const json=await res.json();
      setOutlookCalStatus("connected");
      setOutlookCalAvail(json.availability);
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="outlook"),...(json.events||[])]);
      return true;
    }catch{
      setOutlookCalStatus(prev=>prev==="connected"?"error":null);
      return false;
    }
  },[setOutlookCalStatus,setOutlookCalAvail,setCalEvents]);

  // Core function that fetches OFW ICS events and updates state.
  const fetchOfwCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/ofw/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setOfwCalStatus(null);return false;}
      if(!res.ok){setOfwCalStatus("error");return false;}
      const json=await res.json();
      setOfwCalStatus("connected");
      if(json.uploaded_at) setOfwUploadedAt(json.uploaded_at);
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="ofw"),...(json.events||[])]);
      return true;
    }catch{
      setOfwCalStatus(prev=>prev==="connected"?"error":null);
      return false;
    }
  },[setOfwCalStatus,setOfwUploadedAt,setCalEvents]);

  const fetchYahooCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/yahoo/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setYahooCalStatus(null);return false;}
      if(!res.ok){setYahooCalStatus("error");return false;}
      const json=await res.json();
      setYahooCalStatus("connected");
      setYahooCalAvail(json.availability);
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="yahoo"),...(json.events||[])]);
      return true;
    }catch{
      setYahooCalStatus(prev=>prev==="connected"?"error":null);
      return false;
    }
  },[setYahooCalStatus,setYahooCalAvail,setCalEvents]);

  const fetchCaldavCalEvents=useCallback(async(sessionToken,from,to)=>{
    try{
      const token=sessionToken||(await supabase.auth.getSession()).data?.session?.access_token;
      if(!token) return false;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/caldav/events?tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${token}`}});
      if(res.status===404){setCaldavCalStatus(null);return false;}
      if(!res.ok){setCaldavCalStatus("error");return false;}
      const json=await res.json();
      setCaldavCalStatus("connected");
      setCaldavCalAvail(json.availability);
      setCalEvents(prev=>[...prev.filter(e=>e.source!=="caldav"),...(json.events||[])]);
      return true;
    }catch{
      setCaldavCalStatus(prev=>prev==="connected"?"error":null);
      return false;
    }
  },[setCaldavCalStatus,setCaldavCalAvail,setCalEvents]);

  // â”€â”€ Work schedule CRUD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const fetchWorkSchedules=useCallback(async()=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch('/api/work-schedule/fetch',{headers:{Authorization:`Bearer ${session.access_token}`}});
      if(!res.ok) return;
      const json=await res.json();
      setWorkSchedules(json.schedules||[]);
      setWorkScheduleExceptions(json.exceptions||[]);
    }catch{}
  },[]);

  const saveWorkSchedule=useCallback(async(schedule)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return null;
      const res=await apiFetch('/api/work-schedule/save',{method:'POST',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify(schedule)});
      if(!res.ok) return null;
      const json=await res.json();
      await fetchWorkSchedules();
      return json;
    }catch{return null;}
  },[fetchWorkSchedules]);

  const deleteWorkSchedule=useCallback(async(id)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      await apiFetch('/api/work-schedule/delete',{method:'DELETE',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify({id})});
      await fetchWorkSchedules();
    }catch{}
  },[fetchWorkSchedules]);

  const saveWorkException=useCallback(async(exception)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return null;
      const res=await apiFetch('/api/work-schedule/exception',{method:'POST',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify(exception)});
      if(!res.ok) return null;
      const json=await res.json();
      await fetchWorkSchedules();
      return json;
    }catch{return null;}
  },[fetchWorkSchedules]);

  const deleteWorkException=useCallback(async(id)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      await apiFetch('/api/work-schedule/exception',{method:'DELETE',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify({id})});
      await fetchWorkSchedules();
    }catch{}
  },[fetchWorkSchedules]);

  const fetchAgreements=useCallback(async()=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch('/api/agreements/list',{headers:{Authorization:`Bearer ${session.access_token}`}});
      if(!res.ok) return;
      const json=await res.json();
      setAgreements(json.agreements||[]);
    }catch{}
  },[]);

  const saveAgreement=useCallback(async(agreement)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return null;
      const res=await apiFetch('/api/agreements/upsert',{method:'POST',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify(agreement)});
      if(!res.ok) return null;
      const json=await res.json();
      await fetchAgreements();
      return json;
    }catch{return null;}
  },[fetchAgreements]);

  const deleteAgreement=useCallback(async(id)=>{
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      setAgreements(a=>a.filter(x=>x.id!==id));
      await apiFetch('/api/agreements/delete',{method:'DELETE',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify({id})});
    }catch{}
  },[]);

  // Fetch another user's calendar availability, filtered by their privacy settings.
  // Returns null on error or when not connected.
  const fetchPeerAvailability=useCallback(async(targetUserId,from,to)=>{
    if(!targetUserId) return null;
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return null;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      let url=`/api/availability/fetch?target_user_id=${encodeURIComponent(targetUserId)}&tz=${tz}`;
      if(from) url+=`&from=${encodeURIComponent(from)}`;
      if(to)   url+=`&to=${encodeURIComponent(to)}`;
      const res=await apiFetch(url,{headers:{Authorization:`Bearer ${session.access_token}`}});
      if(!res.ok) return null;
      return await res.json();
    }catch{return null;}
  },[]);

  // Fetch current-week availability for every connected (linked) user so the
  // home page "Available now" strip reflects real calendar data automatically.
  const [linkedAvailMap,setLinkedAvailMap]=useState({});
  const linkedPeopleKey=people.filter(p=>p.linkedUserId).map(p=>`${p.id}:${p.linkedUserId}`).join(",");
  useEffect(()=>{
    if(!userId) return;
    setLinkedAvailMap({}); // Clear stale data before refetching (prevents day-index mismatch after code updates)
    people.filter(p=>p.linkedUserId).forEach(p=>{
      fetchPeerAvailability(p.linkedUserId).then(data=>{
        if(data?.availability) setLinkedAvailMap(prev=>({...prev,[p.id]:data.availability}));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId,linkedPeopleKey]);

  // Derive people with live avail: linked users get avail = free right now per
  // their calendar; non-linked users keep the manual boolean.
  const peopleWithLiveAvail=useMemo(()=>{
    if(!Object.keys(linkedAvailMap).length) return people;
    const now=new Date();
    const di=now.getDay(); // 0=Sunâ€¦6=Sat matches new Sun-first grid
    const h=now.getHours()+now.getMinutes()/60;
    return people.map(p=>{
      const av=linkedAvailMap[p.id];
      if(!av) return p;
      const freeNow=(av[di]||[]).some(([s,e])=>s<=h&&h<e);
      return freeNow===!!p.avail?p:{...p,avail:freeNow};
    });
  },[people,linkedAvailMap]);

  // Fetch Google Calendar availability whenever the user is known.
  // On page load after OAuth redirect (?google_connected=true) the component
  // remounts fresh, so this effect naturally fires and picks up the new tokens.
  useEffect(()=>{
    if(!userId) return;
    // Clean up OAuth redirect params from the URL without triggering a reload
    const params=new URLSearchParams(window.location.search);
    if(params.has("google_connected")||params.has("google_error")){
      window.history.replaceState({},document.title,window.location.pathname);
      // Force account list re-fetch in CalendarsSection after a new account is added
      if(params.has("google_connected")) setGoogleAccounts(null);
    }
    let cancelled=false;
    setGoogleCalStatus("loading");
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token){setGoogleCalStatus(null);return;}
      const ok=await fetchGoogleCalEvents(session.access_token);
      if(cancelled||!ok) return;
      // Register a push-notification watch channel so the webhook can stamp
      // events_updated_at when Google notifies us of changes.
      apiFetch("/api/google/watch",{
        method:"POST",
        headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
        body:JSON.stringify({calendarId:"primary"}),
      }).catch(()=>{/* non-critical â€” incremental sync degrades gracefully */});
    })();
    return()=>{cancelled=true;};
  },[userId,fetchGoogleCalEvents]);

  // Re-fetch Google events when the tab becomes visible again (after â‰¥5 min
  // away) to pick up changes without relying solely on the webhook.
  useEffect(()=>{
    if(!userId) return;
    const onVisible=()=>{
      if(document.visibilityState!=="visible") return;
      if(Date.now()-googleLastFetchRef.current<GOOGLE_REFETCH_MS) return;
      fetchGoogleCalEvents();
    };
    document.addEventListener("visibilitychange",onVisible);
    return()=>document.removeEventListener("visibilitychange",onVisible);
  },[userId,fetchGoogleCalEvents]);

  // Polling fallback: re-fetch Google events every 15 min while the tab is open.
  // Guards against the watch channel being expired, unregistered, or never renewed.
  useEffect(()=>{
    if(!userId||googleCalStatus!=="connected") return;
    const id=setInterval(()=>{fetchGoogleCalEvents();},GOOGLE_POLL_MS);
    return()=>clearInterval(id);
  },[userId,googleCalStatus,fetchGoogleCalEvents]);

  // Fetch iCloud events on page load whenever the user is known.
  // Don't set "loading" upfront â€” fetchICloudCalEvents handles its own status
  // updates, and we don't want a "loading" flash for users who never connected.
  useEffect(()=>{
    if(!userId) return;
    let cancelled=false;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchICloudCalEvents(session.access_token);
    })();
    return()=>{cancelled=true;};
  },[userId,fetchICloudCalEvents]);

  // Fetch Outlook events on page load whenever the user is known.
  useEffect(()=>{
    if(!userId) return;
    // Clean up Outlook OAuth redirect params
    const params=new URLSearchParams(window.location.search);
    if(params.has("outlook_connected")||params.has("outlook_error")){
      window.history.replaceState({},document.title,window.location.pathname);
    }
    let cancelled=false;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchOutlookCalEvents(session.access_token);
    })();
    return()=>{cancelled=true;};
  },[userId,fetchOutlookCalEvents]);

  // Fetch OFW events on page load whenever the user is known.
  useEffect(()=>{
    if(!userId) return;
    let cancelled=false;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchOfwCalEvents(session.access_token);
    })();
    return()=>{cancelled=true;};
  },[userId,fetchOfwCalEvents]);

  useEffect(()=>{
    if(!userId) return;
    let cancelled=false;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchYahooCalEvents(session.access_token);
    })();
    return()=>{cancelled=true;};
  },[userId,fetchYahooCalEvents]);

  useEffect(()=>{
    if(!userId) return;
    let cancelled=false;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchCaldavCalEvents(session.access_token);
    })();
    return()=>{cancelled=true;};
  },[userId,fetchCaldavCalEvents]);

  // Fetch pending connection requests addressed to the current user
  const fetchConnectionRequests=useCallback(async(token)=>{
    if(!token) return;
    try{
      const res=await apiFetch("/api/connections/pending",{headers:{Authorization:`Bearer ${token}`}});
      if(!res.ok) return;
      const json=await res.json();
      const requests=json.requests||[];
      setConnectionRequests(requests);
      // Sync pending requests into notifs so Inbox and Updates always reflect them,
      // even when Supabase Realtime postgres_changes hasn't fired (initial load, polling).
      setNotifs(prev=>{
        const base=prev.filter(n=>!(n.kind==="connection_request"&&n.status==="pending"));
        const fresh=requests.filter(req=>!deletedNotifIdsRef.current.has(`connreq-${req.id}`)).map(req=>({
          id:`connreq-${req.id}`,
          ini:deriveIni(req.from_display_name||"?"),
          type:req.relationship_type||"friend",
          status:"pending",
          subject:`${req.from_display_name||"Someone"} wants to connect`,
          body:`${req.from_display_name||"An Orbit user"} sent you a connection request as ${req.relationship_type||"a connection"}.`,
          time:"Just now",
          read:readIdsRef.current.has(`connreq-${req.id}`),
          kind:"connection_request",
          day:null,start:null,end:null,
          _ts:new Date(req.created_at||Date.now()).getTime(),
        }));
        return [...fresh,...base];
      });
    }catch(_){}
  },[]);

  // Convert accepted scheduling_requests rows to calEvent objects
  function schedReqToCalEvents(req,userId,people){
    const iAmSender=req.from_user_id===userId;
    const otherName=iAmSender?(req.to_display_name||"Someone"):(req.from_display_name||"Someone");
    const otherId=iAmSender?req.to_user_id:req.from_user_id;
    const eventDate=req.counter_date||req.proposed_date;
    const eventStartH=req.counter_start_h??req.proposed_start_h;
    const eventEndH=req.counter_end_h??req.proposed_end_h;
    const matchPerson=people.find(p=>p.linkedUserId===otherId);
    const label=otherName+(req.message?` · ${req.message}`:"");
    const endDateStr=req.proposed_end_date;
    const isAllDay=!!req.all_day;
    // Multi-day all-day: render as a single spanning all-day event with endDate
    if(endDateStr&&endDateStr>eventDate&&isAllDay){
      const d=new Date(eventDate+"T12:00:00");
      return[{
        id:`orbit-sched-${req.id}`,
        source:"orbit",
        type:req.relationship_type||"date",
        day:d.getDay(),
        date:eventDate,
        endDate:endDateStr,
        startH:0,endH:24,
        label,
        person:matchPerson?.id??null,
        allDay:true,
      }];
    }
    // Multi-day timed: expand one event per day
    if(endDateStr&&endDateStr>eventDate){
      const events=[];
      const cur=new Date(eventDate+"T12:00:00");
      const end=new Date(endDateStr+"T12:00:00");
      let idx=0;
      while(cur<=end){
        const iso=cur.toISOString().slice(0,10);
        const isFirst=idx===0;
        const isLast=iso===endDateStr;
        events.push({
          id:`orbit-sched-${req.id}-d${idx}`,
          source:"orbit",
          type:req.relationship_type||"date",
          day:cur.getDay(),
          date:iso,
          startH:isFirst?Number(eventStartH):0,
          endH:isLast?Number(eventEndH):24,
          label:label+(idx===0?` (day 1)`:isLast?" (last day)":" (cont.)"),
          person:matchPerson?.id??null,
          allDay:false,
          _multiDayGroupId:`orbit-sched-${req.id}`,
        });
        cur.setDate(cur.getDate()+1);
        idx++;
      }
      return events;
    }
    // Single-day event
    const d=new Date(eventDate+"T12:00:00");
    return[{
      id:`orbit-sched-${req.id}`,
      source:"orbit",
      type:req.relationship_type||"date",
      day:d.getDay(),
      date:eventDate,
      startH:Number(eventStartH),
      endH:Number(eventEndH),
      label,
      person:matchPerson?.id??null,
    }];
  }

  // Fetch accepted scheduling requests and materialise them as orbit calEvents.
  // from/to are optional YYYY-MM-DD bounds; omit for "all time".
  const fetchOrbitCalEvents=useCallback(async(from,to)=>{
    if(!userId) return;
    try{
      let q=supabase
        .from("scheduling_requests")
        .select("*")
        .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
        .eq("status","accepted");
      // Include multi-day events that overlap the date range
      if(from&&to) q=q.or(`and(proposed_date.gte.${from},proposed_date.lte.${to}),and(proposed_end_date.gte.${from},proposed_date.lte.${to})`);
      else if(from) q=q.or(`proposed_date.gte.${from},proposed_end_date.gte.${from}`);
      else if(to) q=q.lte("proposed_date",to);
      const{data}=await q;
      if(!data) return;
      const events=data.flatMap(req=>schedReqToCalEvents(req,userId,people));
      setCalEvents(prev=>[
        ...prev.filter(e=>!(e.source==="orbit"&&e.id?.startsWith("orbit-sched-"))),
        ...events,
      ]);
    }catch(_){}
  },[userId,people,setCalEvents]);

  const fetchSchedulingRequests=useCallback(async(token)=>{
    if(!token) return;
    try{
      const res=await apiFetch("/api/scheduling/pending",{headers:{Authorization:`Bearer ${token}`}});
      if(!res.ok) return;
      const json=await res.json();
      const requests=json.requests||[];
      setSchedulingRequests(requests);
      setNotifs(prev=>{
        const base=prev.filter(n=>!(n.kind==="scheduling_request"&&n.status==="pending"));
        const fresh=requests.filter(req=>!deletedNotifIdsRef.current.has(`schedreq-${req.id}`)).map(req=>({
          id:`schedreq-${req.id}`,
          ini:deriveIni(req.from_display_name||"?"),
          type:req.relationship_type||"friend",
          status:"pending",
          subject:`${req.from_display_name||"Someone"} proposed a time`,
          body:`${req.from_display_name||"Someone"} wants to meet on ${req.proposed_day}: ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}.${req.message?` "${req.message}"`:""}`,
          time:"Just now",
          read:readIdsRef.current.has(`schedreq-${req.id}`),
          kind:"scheduling_request",
          day:req.proposed_day,
          start:gsFmtH(req.proposed_start_h),
          end:gsFmtH(req.proposed_end_h),
          message:req.message,
          requestId:req.id,
          _ts:new Date(req.created_at||Date.now()).getTime(),
        }));
        return [...fresh,...base];
      });
    }catch(_){}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const updatePerson=useCallback((id,patch)=>{
    setPeople(ps=>ps.map(p=>p.id===id?{...p,...patch}:p));
    if(!userId) return;
    supabase.from("people").select("*").eq("user_id",userId).eq("id",id).single().then(({data})=>{
      const base=data?rowToPerson(data):null;
      const merged=base?{...base,...patch}:null;
      if(!merged) return;
      return supabase.from("people").update(personToRow(merged,userId)).eq("user_id",userId).eq("id",id);
    });
  },[userId]);

  // Fetch responses to scheduling proposals the current user sent.
  // Runs on initial load and in the 30s polling loop so the sender always
  // sees accept/decline even when Supabase Realtime is not configured for
  // the scheduling_requests table.
  const fetchSentSchedulingResponses=useCallback(async()=>{
    if(!userId) return;
    try{
      const {data}=await supabase
        .from("scheduling_requests")
        .select("*")
        .eq("from_user_id",userId)
        .in("status",["accepted","declined","cancelled","recalled","counter_proposed","expired"]);
      if(!data?.length) return;
      // Remove responded IDs from pending set
      setPendingSchedulingIds(prev=>{
        const s=new Set(prev);
        data.forEach(req=>s.delete(req.to_user_id));
        return s;
      });
      // Remove any cancelled events from the calendar immediately
      const cancelledByRecipient=data.filter(r=>r.status==="cancelled");
      if(cancelledByRecipient.length){
        setCalEvents(prev=>prev.filter(e=>!cancelledByRecipient.some(r=>`orbit-sched-${r.id}`===e.id)));
      }
      setNotifs(prev=>{
        const existingIds=new Set(prev.map(n=>n.id));
        // counter_proposed â†’ scheduling_counter notifs (the other party counter-proposed our request)
        const freshCounter=data
          .filter(req=>req.status==="counter_proposed"&&!existingIds.has(`schedcounter-${req.id}`)&&!deletedNotifIdsRef.current.has(`schedcounter-${req.id}`))
          .map(req=>{
            const counterName=req.to_display_name||"Someone";
            return{id:`schedcounter-${req.id}`,ini:deriveIni(counterName),type:req.relationship_type||"friend",status:"pending",subject:`${counterName} counter-proposed a new time`,body:`${counterName} declined your ${req.proposed_day} proposal and suggests ${req.counter_day||"another time"}: ${gsFmtRange(req.counter_start_h,req.counter_end_h)}.${req.counter_message?` "${req.counter_message}"`:""}`,time:notifRelTime(req.updated_at),read:readIdsRef.current.has(`schedcounter-${req.id}`),kind:"scheduling_counter",day:req.counter_day,start:gsFmtH(req.counter_start_h),end:gsFmtH(req.counter_end_h),requestId:req.id,counterDate:req.counter_date,counterStartH:Number(req.counter_start_h),counterEndH:Number(req.counter_end_h),originalDay:req.proposed_day,originalStart:gsFmtH(req.proposed_start_h),originalEnd:gsFmtH(req.proposed_end_h),_ts:new Date(req.updated_at||Date.now()).getTime()};
          });
        // accepted / declined â†’ scheduling_response notifs (skip counter_proposed rows)
        const freshResp=data
          .filter(req=>req.status!=="cancelled"&&req.status!=="recalled"&&req.status!=="counter_proposed"&&req.status!=="expired"&&!existingIds.has(`schedrsp-${req.id}`)&&!deletedNotifIdsRef.current.has(`schedrsp-${req.id}`))
          .map(req=>{
            const accepted=req.status==="accepted";
            const responderName=req.to_display_name||"Someone";
            // When accepting a counter-proposed request, show the counter time in the confirmation
            const displayDay=accepted&&req.counter_date?req.counter_day||req.proposed_day:req.proposed_day;
            const displayRange=accepted&&req.counter_start_h!=null?gsFmtRange(req.counter_start_h,req.counter_end_h):gsFmtRange(req.proposed_start_h,req.proposed_end_h);
            return{id:`schedrsp-${req.id}`,ini:deriveIni(responderName),type:req.relationship_type||"friend",status:accepted?"confirmed":"declined",subject:`${responderName} ${accepted?"confirmed":"declined"} your proposal`,body:`${responderName} ${accepted?"confirmed":"declined"} your proposed time: ${displayDay} ${displayRange}.`,time:notifRelTime(req.updated_at),read:readIdsRef.current.has(`schedrsp-${req.id}`),kind:"scheduling_response",day:displayDay,start:gsFmtH(accepted&&req.counter_start_h!=null?req.counter_start_h:req.proposed_start_h),end:gsFmtH(accepted&&req.counter_end_h!=null?req.counter_end_h:req.proposed_end_h),_ts:new Date(req.updated_at||Date.now()).getTime()};
          });
        // cancelled by the recipient â†’ scheduling_cancelled notifs
        const freshCancel=cancelledByRecipient
          .filter(req=>!existingIds.has(`schedcancel-${req.id}`)&&!deletedNotifIdsRef.current.has(`schedcancel-${req.id}`))
          .map(req=>{
            const canceller=req.to_display_name||"Someone";
            return{id:`schedcancel-${req.id}`,ini:deriveIni(canceller),type:req.relationship_type||"friend",status:"declined",subject:`${canceller} cancelled your plan`,body:req.cancellation_message?`${canceller} cancelled your plan: "${req.cancellation_message}"` : `${canceller} cancelled ${req.proposed_day} ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}.`,time:notifRelTime(req.updated_at),read:readIdsRef.current.has(`schedcancel-${req.id}`),kind:"scheduling_cancelled",day:req.proposed_day,start:gsFmtH(req.proposed_start_h),end:gsFmtH(req.proposed_end_h),_ts:new Date(req.updated_at||Date.now()).getTime()};
          });
        // expired â†’ notify sender that the invite went unanswered
        const freshExpired=data
          .filter(req=>req.status==="expired"&&!existingIds.has(`schedexpired-${req.id}`)&&!deletedNotifIdsRef.current.has(`schedexpired-${req.id}`))
          .map(req=>{
            const name=req.to_display_name||"Someone";
            return{id:`schedexpired-${req.id}`,ini:deriveIni(name),type:req.relationship_type||"friend",status:"expired",subject:`Invite to ${name} expired`,body:`Your ${req.proposed_day} proposal to ${name} (${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}) passed without a response.`,time:notifRelTime(req.updated_at),read:readIdsRef.current.has(`schedexpired-${req.id}`),kind:"scheduling_expired",day:req.proposed_day,start:gsFmtH(req.proposed_start_h),end:gsFmtH(req.proposed_end_h),_ts:new Date(req.updated_at||Date.now()).getTime()};
          });
        const fresh=[...freshCounter,...freshResp,...freshCancel,...freshExpired];
        if(!fresh.length) return prev;
        // Flip scheduling_sent status for responded rows; remove superseded schedcounter notifs when answered
        const updated=prev.map(n=>{
          if(n.kind==="scheduling_sent"){
            const req=data.find(r=>`schedsent-${r.id}`===n.id);
            if(!req) return n;
            if(req.status==="counter_proposed") return{...n,status:"declined"};
            if(req.status==="expired") return{...n,status:"expired"};
            return{...n,status:req.status==="accepted"?"confirmed":"declined"};
          }
          if(n.kind==="scheduling_counter"&&n.requestId){
            // If the counter was answered (accepted/declined), drop the pending card
            const req=data.find(r=>r.id===n.requestId);
            if(req&&(req.status==="accepted"||req.status==="declined")) return null;
          }
          return n;
        }).filter(Boolean);
        return [...fresh,...updated];
      });
      // Add newly accepted requests to the calendar
      const newlyAccepted=data.filter(req=>req.status==="accepted");
      if(newlyAccepted.length){
        setCalEvents(prev=>{
          const newEvts=newlyAccepted
            .filter(req=>!prev.some(e=>e.id===`orbit-sched-${req.id}`||e.id?.startsWith(`orbit-sched-${req.id}-`)))
            .flatMap(req=>schedReqToCalEvents(req,userId,people));
          return newEvts.length?[...prev,...newEvts]:prev;
        });
        // Scheduling→fairness (sender side): the server credits people.act for
        // both partners inside /api/scheduling/respond when the recipient
        // accepts, so here we only optimistically reflect it in local UI state —
        // we no longer persist act from the client (it's server-authoritative).
        newlyAccepted.forEach(req=>{
          if(fairnessAppliedRef.current.has(req.id)) return;
          fairnessAppliedRef.current.add(req.id);
          const matchPerson=people.find(p=>p.linkedUserId===req.to_user_id);
          if(matchPerson) setPeople(ps=>ps.map(p=>p.id===matchPerson.id?{...p,lastSeenAt:new Date().toISOString(),seen:0,act:(p.act||0)+1}:p));
        });
      }
    }catch(_){}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId,people,setCalEvents]);

  // Fetch events cancelled by the other party where the current user was the recipient.
  // Runs on initial load and in the polling loop as a fallback for when Supabase Realtime
  // is not configured for the scheduling_requests table.
  const fetchCancelledByOther=useCallback(async()=>{
    if(!userId) return;
    try{
      const {data}=await supabase
        .from("scheduling_requests")
        .select("*")
        .eq("to_user_id",userId)
        .eq("status","cancelled");
      if(!data?.length) return;
      // Only surface cancellations where the sender (not us) cancelled
      const cancelledBySender=data.filter(r=>r.cancelled_by!==userId);
      if(!cancelledBySender.length) return;
      setCalEvents(prev=>prev.filter(e=>!cancelledBySender.some(r=>e.source==="orbit"&&(e.id===`orbit-sched-${r.id}`||e.id?.startsWith(`orbit-sched-${r.id}-d`)))));
      setNotifs(prev=>{
        const existingIds=new Set(prev.map(n=>n.id));
        const fresh=cancelledBySender
          .filter(req=>!existingIds.has(`schedcancel-${req.id}`))
          .map(req=>({
            id:`schedcancel-${req.id}`,
            ini:deriveIni(req.from_display_name||"?"),
            type:req.relationship_type||"friend",
            status:"declined",
            subject:`${req.from_display_name||"Someone"} cancelled your plan`,
            body:req.cancellation_message?`${req.from_display_name||"Someone"} cancelled your plan: "${req.cancellation_message}"` : `${req.from_display_name||"Someone"} cancelled ${req.proposed_day} ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}.`,
            time:notifRelTime(req.updated_at),
            read:readIdsRef.current.has(`schedcancel-${req.id}`),
            kind:"scheduling_cancelled",
            day:req.proposed_day,
            start:gsFmtH(req.proposed_start_h),
            end:gsFmtH(req.proposed_end_h),
            _ts:new Date(req.updated_at||Date.now()).getTime(),
          }));
        return fresh.length?[...fresh,...prev]:prev;
      });
    }catch(_){}
  },[userId,setCalEvents]);

  // Fetch outcomes of counter-proposals sent by the current user (as original recipient).
  // Detects when the original sender has accepted or declined the counter.
  // NOTE: must be defined before the big connection/scheduling useEffect below so it
  // can appear in that effect's dependency array without hitting the TDZ.
  const fetchCounterProposalOutcomes=useCallback(async()=>{
    if(!userId) return;
    try{
      const {data}=await supabase
        .from("scheduling_requests")
        .select("*")
        .eq("to_user_id",userId)
        .not("counter_date","is",null)
        .in("status",["accepted","declined"]);
      if(!data?.length) return;
      setNotifs(prev=>{
        const existingIds=new Set(prev.map(n=>n.id));
        const fresh=data
          .filter(req=>!existingIds.has(`schedcounterout-${req.id}`))
          .map(req=>{
            const accepted=req.status==="accepted";
            const senderName=req.from_display_name||"Someone";
            return{id:`schedcounterout-${req.id}`,ini:deriveIni(senderName),type:req.relationship_type||"friend",status:accepted?"confirmed":"declined",subject:`${senderName} ${accepted?"accepted":"declined"} your counter-proposal`,body:`${senderName} ${accepted?"accepted":"declined"} your suggested time: ${req.counter_day||req.proposed_day} ${gsFmtRange(req.counter_start_h,req.counter_end_h)}.`,time:notifRelTime(req.updated_at),read:readIdsRef.current.has(`schedcounterout-${req.id}`),kind:"scheduling_counter_outcome",day:req.counter_day,start:gsFmtH(req.counter_start_h),end:gsFmtH(req.counter_end_h),_ts:new Date(req.updated_at||Date.now()).getTime()};
          });
        if(!fresh.length) return prev;
        // Remove the "awaiting" schedcountersent-* notif for answered counters
        const answered=new Set(data.map(r=>`schedcountersent-${r.id}`));
        return [...fresh,...prev.filter(n=>!answered.has(n.id))];
      });
      // Add accepted counter-proposals to calendar (using counter time)
      const accepted=data.filter(r=>r.status==="accepted");
      if(accepted.length){
        setCalEvents(prev=>{
          const newEvts=accepted.filter(r=>!prev.some(e=>e.id===`orbit-sched-${r.id}`||e.id?.startsWith(`orbit-sched-${r.id}-`))).flatMap(r=>schedReqToCalEvents(r,userId,people));
          return newEvts.length?[...prev,...newEvts]:prev;
        });
      }
    }catch(_){}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId,people,setCalEvents]);

  useEffect(()=>{
    if(!userId) return;
    let cancelled=false;
    let channel=null;
    let pollInterval=null;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(cancelled) return;
      if(!session?.access_token) return;
      await fetchConnectionRequests(session.access_token);
      await fetchSchedulingRequests(session.access_token);
      const {data:sentReqs}=await supabase.from("connection_requests").select("id,to_user_id").eq("from_user_id",userId).eq("status","pending");
      if(!cancelled&&sentReqs){setPendingOutgoingRequests(sentReqs);setPendingOutgoingIds(new Set(sentReqs.map(r=>r.to_user_id)));}
      // Load all outbound scheduling proposals (all statuses â†’ Sent tab persists across sessions)
      const {data:allSentSched}=await supabase.from("scheduling_requests").select("*").eq("from_user_id",userId);
      if(!cancelled&&allSentSched){
        const pendingSent=allSentSched.filter(r=>r.status==="pending");
        setPendingSchedulingIds(new Set(pendingSent.map(r=>r.to_user_id)));
        setNotifs(prev=>{
          const base=prev.filter(n=>n.kind!=="scheduling_sent");
          const fresh=allSentSched.filter(req=>req.status!=="cancelled").map(req=>({
            id:`schedsent-${req.id}`,
            ini:deriveIni(req.to_display_name||"?"),
            type:req.relationship_type||"friend",
            status:req.status==="accepted"?"confirmed":req.status==="declined"?"declined":"pending",
            subject:`You proposed ${req.proposed_day}`,
            body:req.status==="pending"
              ?`Waiting for ${req.to_display_name||"them"} to respond. ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}${req.message?` · "${req.message}"`:""}`
              :`${req.to_display_name||"They"} ${req.status==="accepted"?"confirmed":"declined"} your proposal. ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}`,
            time:"Sent",
            read:true,
            kind:"scheduling_sent",
            day:req.proposed_day,
            start:gsFmtH(req.proposed_start_h),
            end:gsFmtH(req.proposed_end_h),
            message:req.message,
            requestId:req.id,
            toUserId:req.to_user_id,
            _ts:new Date(req.created_at||Date.now()).getTime(),
          }));
          return [...base,...fresh];
        });
        // Surface any responses that arrived before this load (covers offline/realtime-disabled case)
        if(!cancelled) await fetchSentSchedulingResponses();
        // Surface any events cancelled by the other party while offline
        if(!cancelled) await fetchCancelledByOther();
        // Surface outcomes of counter-proposals this user sent (as original recipient)
        if(!cancelled) await fetchCounterProposalOutcomes();
        // Hydrate any counter-proposals this user sent (as original recipient) that are still awaiting response
        if(!cancelled){
          const {data:counterSent}=await supabase.from("scheduling_requests").select("*").eq("to_user_id",userId).eq("status","counter_proposed");
          if(!cancelled&&counterSent?.length){
            setNotifs(prev=>{
              const existingIds=new Set(prev.map(n=>n.id));
              const fresh=counterSent.filter(req=>!existingIds.has(`schedcountersent-${req.id}`)).map(req=>({id:`schedcountersent-${req.id}`,ini:deriveIni(req.from_display_name||"?"),type:req.relationship_type||"friend",status:"pending",subject:`Counter-proposal sent for ${req.counter_day||req.proposed_day}`,body:`You counter-proposed ${req.counter_day||"another time"}: ${gsFmtRange(req.counter_start_h,req.counter_end_h)}. Waiting for response.`,time:notifRelTime(req.updated_at),read:true,kind:"scheduling_counter_sent",day:req.counter_day,start:gsFmtH(req.counter_start_h),end:gsFmtH(req.counter_end_h),requestId:req.id,toUserId:req.from_user_id,_ts:new Date(req.updated_at||Date.now()).getTime()}));
              return fresh.length?[...prev.filter(n=>n.kind!=="scheduling_counter_sent"),...fresh]:prev;
            });
          }
        }
        // Populate current week's accepted scheduling events in the calendar
        if(!cancelled) await fetchOrbitCalEvents();
        // Load work schedules
        if(!cancelled) await fetchWorkSchedules();
        // Load agreements
        if(!cancelled) await fetchAgreements();
      }
      // Real-time: new incoming connection requests pop up on home screen + inbox message
      // Real-time: sender sees pending badge removed when recipient accepts/declines
      // Real-time: someone removed the current user from their people list
      channel=supabase
        .channel(`orbit-live-${userId}`)
        .on("postgres_changes",{event:"INSERT",schema:"public",table:"connection_requests",filter:`to_user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          setConnectionRequests(prev=>{
            if(prev.find(r=>r.id===payload.new.id)) return prev;
            return [...prev,payload.new];
          });
          setNotifs(prev=>[{
            id:`connreq-${payload.new.id}`,
            ini:deriveIni(payload.new.from_display_name||"?"),
            type:payload.new.relationship_type||"friend",
            status:"pending",
            subject:`${payload.new.from_display_name||"Someone"} wants to connect`,
            body:`${payload.new.from_display_name||"An Orbit user"} sent you a connection request as ${payload.new.relationship_type||"a connection"}.`,
            time:"Just now",
            read:false,
            kind:"connection_request",
            day:null,start:null,end:null,
            _ts:Date.now(),
          },...prev]);
        })
        .on("postgres_changes",{event:"UPDATE",schema:"public",table:"connection_requests",filter:`from_user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          const updated=payload.new;
          if(updated.status==="accepted"||updated.status==="declined"){
            // Remove pending badge for this outgoing request
            setPendingOutgoingIds(prev=>{const s=new Set(prev);s.delete(updated.to_user_id);return s;});
            setPendingOutgoingRequests(prev=>prev.filter(r=>r.to_user_id!==updated.to_user_id));
            const responderName=updated.to_display_name||"Someone";
            const responderIni=deriveIni(responderName);
            if(updated.status==="accepted"){
              // Re-fetch people to pick up the newly linked person
              supabase.from("people").select("*").eq("user_id",userId).then(({data,error})=>{
                if(!error&&data&&!cancelled) setPeople(data.map(rowToPerson));
              });
              setNotifs(prev=>[{
                id:`connreq-accepted-${updated.id}`,
                ini:responderIni,
                type:updated.relationship_type||"friend",
                status:"confirmed",
                subject:`${responderName} accepted your request`,
                body:`${responderName} accepted your connection request. You're now connected.`,
                time:"Just now",
                read:false,
                kind:"connection_request",
                day:null,start:null,end:null,
                _ts:Date.now(),
              },...prev]);
            } else {
              // Declined â€” notify the requestor
              setNotifs(prev=>[{
                id:`connreq-declined-${updated.id}`,
                ini:responderIni,
                type:updated.relationship_type||"friend",
                status:"declined",
                subject:`${responderName} declined your request`,
                body:`${responderName} declined your connection request.`,
                time:"Just now",
                read:false,
                kind:"connection_request",
                day:null,start:null,end:null,
                _ts:Date.now(),
              },...prev]);
            }
          }
        })
        .on("postgres_changes",{event:"DELETE",schema:"public",table:"people",filter:`user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          setPeople(prev=>prev.filter(p=>p.id!==payload.old.id));
        })
        // Real-time: a linked user updated their profile (name/photo/birthday)
        .on("postgres_changes",{event:"UPDATE",schema:"public",table:"user_profiles"},(payload)=>{
          if(cancelled) return;
          const prof=payload.new;
          setPeople(prev=>{
            const idx=prev.findIndex(p=>p.linkedUserId===prof.user_id);
            if(idx===-1) return prev;
            const updated=[...prev];
            updated[idx]={...updated[idx],
              name:prof.display_name||updated[idx].name,
              photo:prof.photo??updated[idx].photo,
              birthday:prof.birthday??updated[idx].birthday,
            };
            return updated;
          });
        })
        // Real-time: new incoming scheduling proposal
        .on("postgres_changes",{event:"INSERT",schema:"public",table:"scheduling_requests",filter:`to_user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          const req=payload.new;
          setSchedulingRequests(prev=>{
            if(prev.find(r=>r.id===req.id)) return prev;
            return [...prev,req];
          });
          setNotifs(prev=>[{
            id:`schedreq-${req.id}`,
            ini:deriveIni(req.from_display_name||"?"),
            type:req.relationship_type||"friend",
            status:"pending",
            subject:`${req.from_display_name||"Someone"} proposed a time`,
            body:`${req.from_display_name||"Someone"} wants to meet on ${req.proposed_day}: ${gsFmtRange(req.proposed_start_h,req.proposed_end_h)}.${req.message?` "${req.message}"`:""}`,
            time:"Just now",
            read:false,
            kind:"scheduling_request",
            day:req.proposed_day,
            start:gsFmtH(req.proposed_start_h),
            end:gsFmtH(req.proposed_end_h),
            message:req.message,
            requestId:req.id,
            _ts:Date.now(),
          },...prev]);
        })
        // Real-time: sender sees the response when recipient accepts/declines/cancels
        .on("postgres_changes",{event:"UPDATE",schema:"public",table:"scheduling_requests",filter:`from_user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          const updated=payload.new;
          if(updated.status==="accepted"||updated.status==="declined"){
            setPendingSchedulingIds(prev=>{const s=new Set(prev);s.delete(updated.to_user_id);return s;});
            // Immediately add to calendar when accepted
            if(updated.status==="accepted"){
              setCalEvents(prev=>{
                if(prev.some(e=>e.id===`orbit-sched-${updated.id}`||e.id?.startsWith(`orbit-sched-${updated.id}-`))) return prev;
                return [...prev,...schedReqToCalEvents(updated,userId,people)];
              });
            }
            const responderName=updated.to_display_name||"Someone";
            const accepted=updated.status==="accepted";
            setNotifs(prev=>{
              const withoutSent=prev.filter(n=>n.id!==`schedsent-${updated.id}`);
              return [{
                id:`schedrsp-${updated.id}`,
                ini:deriveIni(responderName),
                type:updated.relationship_type||"friend",
                status:accepted?"confirmed":"declined",
                subject:`${responderName} ${accepted?"confirmed":"declined"} your proposal`,
                body:`${responderName} ${accepted?"confirmed":"declined"} your proposed time: ${updated.proposed_day} ${gsFmtRange(updated.proposed_start_h,updated.proposed_end_h)}.`,
                time:"Just now",
                read:false,
                kind:"scheduling_response",
                day:updated.proposed_day,
                start:gsFmtH(updated.proposed_start_h),
                end:gsFmtH(updated.proposed_end_h),
                _ts:Date.now(),
              },...withoutSent];
            });
          }
          // Recipient counter-proposed â€” remove schedsent notif, add scheduling_counter notif for sender
          if(updated.status==="counter_proposed"){
            setPendingSchedulingIds(prev=>{const s=new Set(prev);s.delete(updated.to_user_id);return s;});
            const counterName=updated.to_display_name||"Someone";
            setNotifs(prev=>{
              const withoutSent=prev.filter(n=>n.id!==`schedsent-${updated.id}`);
              return [{id:`schedcounter-${updated.id}`,ini:deriveIni(counterName),type:updated.relationship_type||"friend",status:"pending",subject:`${counterName} counter-proposed a new time`,body:`${counterName} declined your ${updated.proposed_day} proposal and suggests ${updated.counter_day||"another time"}: ${gsFmtRange(updated.counter_start_h,updated.counter_end_h)}.${updated.counter_message?` "${updated.counter_message}"`:""}`,time:"Just now",read:false,kind:"scheduling_counter",day:updated.counter_day,start:gsFmtH(updated.counter_start_h),end:gsFmtH(updated.counter_end_h),requestId:updated.id,counterDate:updated.counter_date,counterStartH:Number(updated.counter_start_h),counterEndH:Number(updated.counter_end_h),originalDay:updated.proposed_day,originalStart:gsFmtH(updated.proposed_start_h),originalEnd:gsFmtH(updated.proposed_end_h),_ts:Date.now()},...withoutSent];
            });
          }
          // Recipient cancelled a confirmed event â€” remove from calendar and notify sender
          if(updated.status==="cancelled"&&updated.cancelled_by===updated.to_user_id){
            setCalEvents(prev=>prev.filter(e=>e.id!==`orbit-sched-${updated.id}`));
            const canceller=updated.to_display_name||"Someone";
            setNotifs(prev=>{
              const withoutSent=prev.filter(n=>n.id!==`schedsent-${updated.id}`);
              return [{id:`schedcancel-${updated.id}`,ini:deriveIni(canceller),type:updated.relationship_type||"friend",status:"declined",subject:`${canceller} cancelled your plan`,body:updated.cancellation_message?`${canceller} cancelled your plan: "${updated.cancellation_message}"` : `${canceller} cancelled ${updated.proposed_day} ${gsFmtRange(updated.proposed_start_h,updated.proposed_end_h)}.`,time:"Just now",read:false,kind:"scheduling_cancelled",day:updated.proposed_day,start:gsFmtH(updated.proposed_start_h),end:gsFmtH(updated.proposed_end_h),_ts:Date.now()},...withoutSent];
            });
          }
        })
        // Real-time: sender cancels a confirmed event â€” notify the recipient
        // Also: sender accepted/declined the recipient's counter-proposal
        .on("postgres_changes",{event:"UPDATE",schema:"public",table:"scheduling_requests",filter:`to_user_id=eq.${userId}`},(payload)=>{
          if(cancelled) return;
          const updated=payload.new;
          if(updated.status==="cancelled"&&updated.cancelled_by===updated.from_user_id){
            setCalEvents(prev=>prev.filter(e=>e.id!==`orbit-sched-${updated.id}`));
            const canceller=updated.from_display_name||"Someone";
            setNotifs(prev=>{
              const withoutReq=prev.filter(n=>n.id!==`schedreq-${updated.id}`);
              return [{id:`schedcancel-${updated.id}`,ini:deriveIni(canceller),type:updated.relationship_type||"friend",status:"declined",subject:`${canceller} cancelled your plan`,body:updated.cancellation_message?`${canceller} cancelled your plan: "${updated.cancellation_message}"` : `${canceller} cancelled ${updated.proposed_day} ${gsFmtRange(updated.proposed_start_h,updated.proposed_end_h)}.`,time:"Just now",read:false,kind:"scheduling_cancelled",day:updated.proposed_day,start:gsFmtH(updated.proposed_start_h),end:gsFmtH(updated.proposed_end_h),_ts:Date.now()},...withoutReq];
            });
          }
          // Original sender accepted or declined the counter-proposal
          if(updated.counter_date&&(updated.status==="accepted"||updated.status==="declined")){
            const accepted=updated.status==="accepted";
            const senderName=updated.from_display_name||"Someone";
            if(accepted){
              setCalEvents(prev=>{
                if(prev.some(e=>e.id===`orbit-sched-${updated.id}`||e.id?.startsWith(`orbit-sched-${updated.id}-`))) return prev;
                return [...prev,...schedReqToCalEvents(updated,userId,people)];
              });
            }
            setNotifs(prev=>{
              const withoutCounter=prev.filter(n=>n.id!==`schedcountersent-${updated.id}`);
              return [{id:`schedcounterout-${updated.id}`,ini:deriveIni(senderName),type:updated.relationship_type||"friend",status:accepted?"confirmed":"declined",subject:`${senderName} ${accepted?"accepted":"declined"} your counter-proposal`,body:`${senderName} ${accepted?"accepted":"declined"} your suggested time: ${updated.counter_day||updated.proposed_day} ${gsFmtRange(updated.counter_start_h,updated.counter_end_h)}.`,time:"Just now",read:false,kind:"scheduling_counter_outcome",day:updated.counter_day,start:gsFmtH(updated.counter_start_h),end:gsFmtH(updated.counter_end_h),_ts:Date.now()},...withoutCounter];
            });
          }
        })
        .subscribe();
      // Polling fallback: re-fetch pending requests every 30s for environments
      // where Supabase Realtime is not enabled for this table
      pollInterval=setInterval(async()=>{
        if(cancelled) return;
        const {data:{session:s}}=await supabase.auth.getSession();
        if(s?.access_token){
          await fetchConnectionRequests(s.access_token);
          await fetchSchedulingRequests(s.access_token);
        }
        await fetchSentSchedulingResponses();
        await fetchCancelledByOther();
        await fetchCounterProposalOutcomes();
      },30000);
    })();
    return()=>{
      cancelled=true;
      if(channel) supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId,fetchConnectionRequests,fetchSchedulingRequests,fetchSentSchedulingResponses,fetchCancelledByOther,fetchOrbitCalEvents,fetchCounterProposalOutcomes]);

  // Respond to an incoming connection request (accept or decline)
  const respondToConnectionRequest=useCallback(async(requestId,action)=>{
    // Optimistically remove from UI immediately
    setConnectionRequests(prev=>prev.filter(r=>r.id!==requestId));
    setNotifs(prev=>prev.filter(n=>n.id!==`connreq-${requestId}`));
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch("/api/connections/respond",{
        method:"PATCH",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
        body:JSON.stringify({request_id:requestId,action}),
      });
      if(res.ok&&action==="accept"){
        // Re-fetch people to pick up the newly added person
        const {data,error}=await supabase.from("people").select("*").eq("user_id",userId);
        if(!error&&data) setPeople(data.map(rowToPerson));
      }
    }catch(_){
      // If it failed, restore the request
      fetchConnectionRequests((await supabase.auth.getSession()).data.session?.access_token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId,fetchConnectionRequests]);

  // Rescind (cancel) a pending outgoing connection request
  const rescindConnectionRequest=useCallback(async(toUserId)=>{
    // Optimistically clear the pending state
    setPendingOutgoingIds(prev=>{const s=new Set(prev);s.delete(toUserId);return s;});
    setPendingOutgoingRequests(prev=>prev.filter(r=>r.to_user_id!==toUserId));
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch("/api/connections/rescind",{
        method:"DELETE",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
        body:JSON.stringify({to_user_id:toUserId}),
      });
      if(!res.ok){
        // Restore on failure
        const {data:sentReqs}=await supabase.from("connection_requests").select("id,to_user_id").eq("from_user_id",userId).eq("status","pending");
        if(sentReqs){setPendingOutgoingRequests(sentReqs);setPendingOutgoingIds(new Set(sentReqs.map(r=>r.to_user_id)));}
      }
    }catch(_){}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[userId]);

  // Respond to an incoming scheduling proposal (accept or decline)
  const respondToSchedulingRequest=useCallback(async(requestId,action)=>{
    // Find request data before removing from state so we can add to calendar
    const req=schedulingRequests.find(r=>r.id===requestId);
    setSchedulingRequests(prev=>prev.filter(r=>r.id!==requestId));
    setNotifs(prev=>prev.filter(n=>n.id!==`schedreq-${requestId}`));
    // Immediately add to calendar if accepting
    if(action==="accept"&&req){
      setCalEvents(prev=>{
        if(prev.find(e=>e.id===`orbit-sched-${req.id}`)) return prev;
        return [...prev,...schedReqToCalEvents(req,userId,people)];
      });
      // Scheduling→fairness: the server credits people.act for both partners in
      // /api/scheduling/respond (called just below), so only reflect it
      // optimistically in local UI state — act is no longer written from the client.
      const otherId=req.from_user_id===userId?req.to_user_id:req.from_user_id;
      const matchPerson=people.find(p=>p.linkedUserId===otherId);
      if(matchPerson) setPeople(ps=>ps.map(p=>p.id===matchPerson.id?{...p,lastSeenAt:new Date().toISOString(),seen:0,act:(p.act||0)+1}:p));
    }
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch("/api/scheduling/respond",{
        method:"PATCH",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
        body:JSON.stringify({request_id:requestId,action}),
      });
      if(!res.ok) fetchSchedulingRequests((await supabase.auth.getSession()).data.session?.access_token);
    }catch(_){
      fetchSchedulingRequests((await supabase.auth.getSession()).data.session?.access_token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[fetchSchedulingRequests,schedulingRequests,userId,people,setCalEvents]);

  // Send a scheduling proposal to a linked Orbit user
  const sendSchedulingRequest=useCallback(async({toUserId,relationshipType,toDisplayName,proposedDay,proposedDate,proposedStartH,proposedEndH,message,allDay,title,proposedEndDate,recurrence})=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session) throw new Error("Not authenticated");
    const body={to_user_id:toUserId,relationship_type:relationshipType,to_display_name:toDisplayName,proposed_date:proposedDate,proposed_day:proposedDay,proposed_start_h:proposedStartH,proposed_end_h:proposedEndH,message};
    if(allDay) body.all_day=true;
    if(title) body.title=title;
    if(proposedEndDate) body.proposed_end_date=proposedEndDate;
    if(recurrence) body.recurrence=recurrence;
    const res=await apiFetch("/api/scheduling/request",{
      method:"POST",
      headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
      body:JSON.stringify(body),
    });
    if(!res.ok){const j=await res.json().catch(()=>({}));throw new Error(j.error||"Failed to send");}
    const json=await res.json();
    if(toUserId) setPendingSchedulingIds(prev=>new Set([...prev,toUserId]));
    setNotifs(prev=>[{
      id:`schedsent-${json.id}`,
      ini:deriveIni(toDisplayName||"?"),
      type:relationshipType||"friend",
      status:"pending",
      subject:`You proposed ${proposedDay}`,
      body:`Waiting for ${toDisplayName||"them"} to respond. ${gsFmtRange(proposedStartH,proposedEndH)}${message?` · "${message}"`:""}`,
      time:"Just now",
      read:true,
      kind:"scheduling_sent",
      day:proposedDay,
      start:gsFmtH(proposedStartH),
      end:gsFmtH(proposedEndH),
      message,
      requestId:json.id,
      toUserId,
    },...prev]);
    return json;
  },[]);

  // Recall (cancel) a pending outgoing scheduling proposal before the recipient responds
  const recallSchedulingRequest=useCallback(async(requestId,toUserId)=>{
    // Optimistically remove from state
    setPendingSchedulingIds(prev=>{const s=new Set(prev);s.delete(toUserId);return s;});
    setNotifs(prev=>prev.filter(n=>n.id!==`schedsent-${requestId}`));
    // Remove from calendar (covers both single-day and multi-day per-day expansions)
    setCalEvents(prev=>prev.filter(e=>!(e.source==="orbit"&&(e.id===`orbit-sched-${requestId}`||e.id?.startsWith(`orbit-sched-${requestId}-d`)))));
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const res=await apiFetch("/api/scheduling/cancel",{
        method:"PATCH",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
        body:JSON.stringify({request_id:requestId}),
      });
      if(!res.ok){
        // Restore on failure by re-fetching
        if(session) await fetchSentSchedulingResponses();
      }
    }catch(_){
      const {data:{session}}=await supabase.auth.getSession();
      if(session) await fetchSentSchedulingResponses();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[fetchSentSchedulingResponses]);

  // Send a counter-proposal: update the original request with counter_* fields (status â†’ counter_proposed)
  const counterProposeSchedulingRequest=useCallback(async(requestId,{date,day,startH,endH,message,toUserId,toDisplayName,relType})=>{
    // Optimistically: remove from incoming scheduling_requests, remove schedreq-* notif, add schedcountersent-* notif
    setSchedulingRequests(prev=>prev.filter(r=>r.id!==requestId));
    setNotifs(prev=>[{
      id:`schedcountersent-${requestId}`,
      ini:deriveIni(toDisplayName||"?"),
      type:relType||"friend",
      status:"pending",
      subject:`Counter-proposal sent for ${day||date}`,
      body:`You counter-proposed ${day||date}: ${gsFmtRange(startH,endH)}${message?` · "${message}"`:""}. Waiting for response.`,
      time:"Just now",
      read:true,
      kind:"scheduling_counter_sent",
      day,
      start:gsFmtH(startH),
      end:gsFmtH(endH),
      message,
      requestId,
      toUserId,
    },...prev.filter(n=>n.id!==`schedreq-${requestId}`)]);
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) throw new Error("Not authenticated");
      const res=await apiFetch("/api/scheduling/respond",{
        method:"PATCH",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session.access_token}`},
        body:JSON.stringify({request_id:requestId,action:"counter_propose",counter_date:date,counter_day:day,counter_start_h:startH,counter_end_h:endH,counter_message:message||null}),
      });
      if(!res.ok){const j=await res.json().catch(()=>({}));throw new Error(j.error||"Failed to send counter-proposal");}
    }catch(e){
      // Rollback: restore original incoming request notification
      fetchSchedulingRequests((await supabase.auth.getSession()).data.session?.access_token);
      setNotifs(prev=>prev.filter(n=>n.id!==`schedcountersent-${requestId}`));
      throw e;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[fetchSchedulingRequests]);

  const removePerson=useCallback((id)=>{
    const prev=[];
    setPeople(ps=>{
      prev.push(ps);
      return ps.filter(p=>p.id!==id);
    });
    setCalEvents(evs=>evs.filter(e=>e.person!==id));
    if(!userId) return;
    supabase.auth.getSession().then(({data:{session}})=>{
      apiFetch("/api/connections/remove",{
        method:"DELETE",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${session?.access_token}`},
        body:JSON.stringify({person_id:id}),
      }).then(r=>{
        if(!r.ok){
          const snap=prev[0];
          if(snap) setPeople(snap);
          setNotifs(ns=>[{
            id:`err-remove-${Date.now()}`,
            ini:"!",
            type:"friend",
            kind:"info",
            subject:"Couldn't remove contact â€” please check your connection and try again.",
            time:new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}),
            read:false,
          },...ns]);
        }
      });
    });
  },[userId]);

  const addPerson=useCallback((person)=>{
    const tmpId="tmp_"+Date.now();
    const optimistic={...person,id:tmpId};
    setPeople(ps=>[...ps,optimistic]);
    // Optimistically mark as pending immediately so the badge shows without waiting for the DB round-trip
    if(person.linkedUserId) setPendingOutgoingIds(prev=>new Set([...prev,person.linkedUserId]));
    if(!userId) return;
    supabase.from("people").insert(personToRow(person,userId)).select("*").single().then(async({data,error})=>{
      if(error){
        setPeople(ps=>ps.filter(p=>p.id!==tmpId));
        // Rollback optimistic pending state on error
        if(person.linkedUserId) setPendingOutgoingIds(prev=>{const s=new Set(prev);s.delete(person.linkedUserId);return s;});
        setNotifs(ns=>[{
          id:`err-add-${Date.now()}`,
          ini:"!",
          type:"friend",
          kind:"info",
          subject:"Couldn't save contact â€” please check your connection and try again.",
          time:new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}),
          read:false,
        },...ns]);
        return;
      }
      const saved=rowToPerson(data);
      setPeople(ps=>ps.map(p=>p.id===tmpId?saved:p));
    });
  },[userId]);

  const [notifs,setNotifs]=useState([]);
  // Mirror the deleted-notif IDs that InboxContent persists to localStorage so
  // polling functions can filter them out and stop re-adding dismissed items.
  const deletedNotifIdsRef=useRef(null);
  if(deletedNotifIdsRef.current===null){
    try{const v=localStorage.getItem("orbit_deleted_notifs");deletedNotifIdsRef.current=new Set((v?JSON.parse(v):[]).map(n=>n.id));}
    catch{deletedNotifIdsRef.current=new Set();}
  }
  // Called by InboxContent whenever the user deletes a notif so the ref stays in sync.
  const notifyNotifDeleted=useCallback((id)=>{deletedNotifIdsRef.current.add(id);},[]);
  const readIdsRef=useRef(null);
  if(readIdsRef.current===null){
    try{readIdsRef.current=new Set(JSON.parse(localStorage.getItem("orbit_read_notif_ids")||"[]"));}
    catch{readIdsRef.current=new Set();}
  }
  // Debounced sync of read IDs to Supabase (batches rapid reads into one write)
  const readSyncTimer=useRef(null);
  const userIdRef=useRef(userId);
  userIdRef.current=userId;
  const syncReadToSupabase=useCallback(()=>{
    clearTimeout(readSyncTimer.current);
    readSyncTimer.current=setTimeout(()=>{
      const uid=userIdRef.current;
      if(!uid) return;
      const ids=[...readIdsRef.current];
      supabase.from("user_profiles").update({read_notif_ids:ids}).eq("user_id",uid).then(()=>{});
    },1000);
  },[]);
  const persistRead=useCallback((id)=>{
    readIdsRef.current.add(id);
    try{localStorage.setItem("orbit_read_notif_ids",JSON.stringify([...readIdsRef.current]));}catch{}
    setNotifs(ns=>ns.map(x=>x.id===id?{...x,read:true}:x));
    syncReadToSupabase();
  },[syncReadToSupabase]);
  const persistReadAll=useCallback(()=>{
    setNotifs(ns=>{ns.forEach(n=>readIdsRef.current.add(n.id));try{localStorage.setItem("orbit_read_notif_ids",JSON.stringify([...readIdsRef.current]));}catch{}return ns.map(n=>({...n,read:true}));});
    syncReadToSupabase();
  },[syncReadToSupabase]);

  // Sync app icon badge with unread notification count (native only)
  useEffect(()=>{
    if(!Capacitor.isNativePlatform()) return;
    const count=notifs.filter(n=>!n.read).length;
    Badge.set({count}).catch(()=>{});
  },[notifs]);
  const [compact,setCompact]=useState(()=>{try{return localStorage.getItem("orbit_compact")==="true";}catch{return false;}});
  const [themeKey,setThemeKey]=useState(()=>{try{return localStorage.getItem("orbit_theme")||"parchment";}catch{return"parchment";}});
  const [customTheme,setCustomTheme]=useState(()=>{try{const v=localStorage.getItem("orbit_custom_theme");return v?JSON.parse(v):null;}catch{return null;}});
  const [accounts,setAccounts]=useState(()=>{try{const v=localStorage.getItem("orbit_accounts");return v?JSON.parse(v):[];}catch{return[];}});
  // Apply theme by mutating T in place so all components re-render with new colors
  const activeTheme=themeKey==="custom"&&customTheme?customTheme:THEMES[themeKey]||THEMES.parchment;
  Object.assign(T,activeTheme);
  const nav=useCallback((s,personId=null,calDay=null,notifId=null,schedType=null,schedMode=null,pplType=null)=>{setScreen(s);setSchedulePersonId(personId);setCalendarDay(calDay);setInboxNotifId(notifId);setScheduleType(schedType);setScheduleMode(schedMode);setPeopleType(pplType);
    // Always remount schedule so draft state (title, message, slot) never bleeds between sessions.
    // For other screens bump only when navigating with no deep-link args.
    if(s==="schedule"){setNavResets(r=>({...r,schedule:(r.schedule||0)+1}));}
    else if(!personId&&calDay==null&&!notifId&&!schedType&&!schedMode&&!pplType){setNavResets(r=>({...r,[s]:(r[s]||0)+1}));}
  },[]);
  const isEntry=screen==="onboarding"||screen==="second-user";

  // Navigate when a tapped push notification fires push:navigate
  useEffect(()=>{
    const handle=(e)=>{
      const {screen:s,personId,notifId}=e.detail||{};
      if(s) nav(s,personId||null,null,notifId||null);
    };
    window.addEventListener('push:navigate',handle);
    return()=>window.removeEventListener('push:navigate',handle);
  },[nav]);

  // Skip onboarding only when the user has already explicitly set their name.
  // After a Google OAuth round-trip (?google_connected=true), land on Settings → Calendars.
  useEffect(()=>{try{localStorage.setItem("orbit_theme",themeKey);}catch{}},[themeKey]);
  useEffect(()=>{try{localStorage.setItem("orbit_compact",String(compact));}catch{}},[compact]);
  useEffect(()=>{try{localStorage.setItem("orbit_prefs",JSON.stringify(prefs));}catch{}},[prefs]);
  useEffect(()=>{try{localStorage.setItem("orbit_notif_settings",JSON.stringify(notifSettings));}catch{}},[notifSettings]);
  useEffect(()=>{try{localStorage.setItem("orbit_priv",JSON.stringify(priv));}catch{}},[priv]);
  // Sync privacy settings to Supabase so /api/availability/fetch can read them server-side
  useEffect(()=>{
    if(!userId) return;
    supabase.from("user_profiles").update({priv,updated_at:new Date().toISOString()}).eq("user_id",userId).then(()=>{});
  },[priv,userId]);
  // Sync notification preferences to Supabase so API endpoints can respect them
  useEffect(()=>{
    if(!userId) return;
    supabase.from("user_profiles").update({notif_prefs:notifPrefs,updated_at:new Date().toISOString()}).eq("user_id",userId).then(()=>{});
  },[notifPrefs,userId]);
  useEffect(()=>{try{localStorage.setItem("orbit_hide_offline",String(hideOffline));}catch{}},[hideOffline]);
  useEffect(()=>{try{localStorage.setItem("orbit_block_notes",String(blockNotes));}catch{}},[blockNotes]);
  useEffect(()=>{try{localStorage.setItem("orbit_accounts",JSON.stringify(accounts));}catch{}},[accounts]);
  useEffect(()=>{try{localStorage.setItem("orbit_cal_names",JSON.stringify(calendarCustomNames));}catch{}},[calendarCustomNames]);

  // Persist the active screen so a reload returns to the same page.
  // Also fire a Plausible pageview-equivalent for each screen change.
  useEffect(()=>{
    const MAIN_SCREENS=["home","people","schedule","calendars","inbox","settings"];
    if(MAIN_SCREENS.includes(screen)){
      try{localStorage.setItem(LS_SCREEN_KEY,screen);}catch{}
      trackEvent("pageview", { screen });
    }
  },[screen]);

  useEffect(()=>{
    if(!peopleLoaded||!userId) return;
    // After any OAuth redirect, always land on Settings â†’ Calendars so the user
    // can see the connected status immediately â€” even if they haven't set their
    // display name yet (needsNamePrompt). Not checking this first is the #1 cause
    // of the "connected but shows not connected" loop.
    if(cameFromGoogleOAuth||cameFromOutlookOAuth||cameFromYahooOAuth||cameFromCaldavConnect){
      setScreen("settings");
      return;
    }
    if(inviteToken){
      setScreen("second-user");
      // Remove the invite param from the URL so refreshing doesn't re-trigger
      try{const u=new URL(window.location.href);u.searchParams.delete("invite");window.history.replaceState({},"",u.toString());}catch{}
      return;
    }
    // For non-OAuth loads, restore the saved screen only once the name is set
    // so we don't yank the user out of the onboarding flow.
    if(!needsNamePrompt(user)){
      let saved="home";
      try{saved=localStorage.getItem(LS_SCREEN_KEY)||"home";}catch{}
      setScreen(saved);
    }
  // `user` is included so we can check Supabase metadata for returning users,
  // but the effect only fires meaningfully on the initial load (peopleLoaded
  // goes true once). Subsequent user updates (e.g. profile save) won't re-run
  // the needsNamePrompt branch because the name is already in localStorage.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[peopleLoaded,userId]);

  // Show profile completion modal when the user is logged in, has a name set,
  // but is missing phone or birthday. Fires once per session so it re-prompts
  // on the next login if info is still missing.
  useEffect(()=>{
    if(!userId||!peopleLoaded||profileModalDismissed||needsNamePrompt(user)) return;
    const meta=user?.user_metadata||{};
    if(!meta.phone||!meta.birthday){
      const t=setTimeout(()=>setShowProfileModal(true),350);
      return()=>clearTimeout(t);
    }
  },[userId,peopleLoaded,user,profileModalDismissed]);

  // Show first-run walkthrough once the user is set up and no modal is open.
  // Runs after profile modal is dismissed (or immediately if profile is complete).
  useEffect(()=>{
    if(!userId||!peopleLoaded||showProfileModal||isEntry) return;
    try{ if(localStorage.getItem(WALKTHROUGH_KEY)) return; }catch{}
    // Small delay so the home screen renders fully first
    const t=setTimeout(()=>setShowWalkthrough(true),600);
    return()=>clearTimeout(t);
  },[userId,peopleLoaded,showProfileModal,isEntry]);

  // The monthly reset of people.act is now server-authoritative: the
  // monthly-summary cron (api/notifications/monthly-summary.js) zeroes act for
  // all tracked people on the 1st of each month. The client no longer writes
  // act (the DB blocks client act writes — see security_rls_hardening.sql), so
  // there is no client-side reset here anymore.

  if(!peopleLoaded){
    return(
      <div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",background:"#F7F2EB",fontFamily:"'DM Sans',sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');`}</style>
        <div style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.75)",border:"1px solid rgba(228,216,200,0.9)",padding:"14px 16px",borderRadius:16,boxShadow:"0 10px 40px rgba(60,40,20,.10)"}}>
          <img src="/orbit-logo.png" alt="Orbit" style={{width:28,height:28,objectFit:"contain",flexShrink:0}}/>
          <div>
            <div style={{fontFamily:"'Crimson Pro',serif",fontSize:18,color:"#1E1008"}}>orbit</div>
            <div style={{fontSize:12,color:"#A08870"}}>Loading your people...</div>
          </div>
        </div>
      </div>
    );
  }

  return(
    <PeopleContext.Provider value={{people:peopleWithLiveAvail,removePerson,addPerson,updatePerson,notifs,setNotifs,persistRead,persistReadAll,notifyNotifDeleted,compact,setCompact,themeKey,setThemeKey,customTheme,setCustomTheme,agreements,setAgreements,fetchAgreements,saveAgreement,deleteAgreement,calEvents,setCalEvents,user,setUser,userId,googleCalAvail,googleCalStatus,setGoogleCalAvail,setGoogleCalStatus,fetchGoogleCalEvents,googleAccounts,setGoogleAccounts,icloudCalStatus,setIcloudCalStatus,icloudCalAvail,setIcloudCalAvail,icloudCalendars,setIcloudCalendars,icloudSelectedCalIds,setIcloudSelectedCalIds,fetchICloudCalEvents,outlookCalStatus,setOutlookCalStatus,outlookCalAvail,setOutlookCalAvail,outlookCalendars,setOutlookCalendars,outlookSelectedCalIds,setOutlookSelectedCalIds,fetchOutlookCalEvents,ofwCalStatus,setOfwCalStatus,ofwUploadedAt,setOfwUploadedAt,fetchOfwCalEvents,prefs,setPrefs,notifSettings,setNotifSettings,notifPrefs,setNotifPrefs,priv,setPriv,hideOffline,setHideOffline,blockNotes,setBlockNotes,privOverrides,setPrivOverrides,accounts,setAccounts,yahooCalStatus,setYahooCalStatus,yahooCalAvail,setYahooCalAvail,yahooCalendars,setYahooCalendars,yahooSelectedCalIds,setYahooSelectedCalIds,fetchYahooCalEvents,caldavCalStatus,setCaldavCalStatus,caldavCalAvail,setCaldavCalAvail,caldavCalendars,setCaldavCalendars,caldavSelectedCalIds,setCaldavSelectedCalIds,fetchCaldavCalEvents,calendarCustomNames,setCalendarCustomNames,connectionRequests,setConnectionRequests,respondToConnectionRequest,pendingOutgoingIds,pendingOutgoingRequests,rescindConnectionRequest,fetchPeerAvailability,schedulingRequests,setSchedulingRequests,pendingSchedulingIds,respondToSchedulingRequest,sendSchedulingRequest,recallSchedulingRequest,counterProposeSchedulingRequest,fetchSchedulingRequests,fetchSentSchedulingResponses,fetchCounterProposalOutcomes,fetchOrbitCalEvents,workSchedules,setWorkSchedules,workScheduleExceptions,setWorkScheduleExceptions,fetchWorkSchedules,saveWorkSchedule,deleteWorkSchedule,saveWorkException,deleteWorkException}}>
    <div style={{height:"100%",overflow:"hidden",display:"flex",flexDirection:"column",background:"#EDE5D8",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        .orbit-cal-timegrid{scrollbar-width:none;-ms-overflow-style:none;}
        .orbit-cal-timegrid::-webkit-scrollbar{display:none;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes popIn{from{opacity:0;transform:scale(.93)}to{opacity:1;transform:scale(1)}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes scanLine{0%{top:8%}50%{top:84%}100%{top:8%}}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 4px rgba(193,123,78,0.2),0 0 0 8px rgba(193,123,78,0.1)}50%{box-shadow:0 0 0 7px rgba(193,123,78,0.15),0 0 0 14px rgba(193,123,78,0.06)}}
      `}</style>

      {/* Viewport — native uses MobileShell; web uses DesktopShell */}
      <div style={{flex:1,display:"flex",alignItems:"stretch",overflow:"hidden"}}>
        {Capacitor.isNativePlatform()
          ? <MobileShell screen={screen} onNav={nav} schedulePersonId={schedulePersonId} scheduleType={scheduleType} scheduleMode={scheduleMode} calendarDay={calendarDay} inboxNotifId={inboxNotifId} peopleType={peopleType} navResets={navResets} settingsInitialSection={(cameFromGoogleOAuth||cameFromOutlookOAuth||cameFromYahooOAuth||cameFromCaldavConnect)?"calendars":undefined} googleOAuthError={googleOAuthError} outlookOAuthError={outlookOAuthError} yahooOAuthError={yahooOAuthError} caldavConnectError={caldavConnectError} inviteToken={inviteToken} onTakeTour={()=>{nav("home");setShowWalkthrough(true);}}/>
          : <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
              <DesktopShell screen={screen} onNav={nav} schedulePersonId={schedulePersonId} scheduleType={scheduleType} scheduleMode={scheduleMode} calendarDay={calendarDay} inboxNotifId={inboxNotifId} peopleType={peopleType} navResets={navResets} settingsInitialSection={(cameFromGoogleOAuth||cameFromOutlookOAuth||cameFromYahooOAuth||cameFromCaldavConnect)?"calendars":undefined} googleOAuthError={googleOAuthError} outlookOAuthError={outlookOAuthError} yahooOAuthError={yahooOAuthError} caldavConnectError={caldavConnectError} inviteToken={inviteToken} onTakeTour={()=>{nav("home");setShowWalkthrough(true);}}/>
            </div>
        }
      </div>
    </div>
    {/* Profile setup modal â€” rendered outside the main scroll container so it
        overlays the entire viewport including the chrome bar */}
    {showProfileModal&&<ProfileSetupModal onClose={()=>{setShowProfileModal(false);setProfileModalDismissed(true);}}/>}
    {showWalkthrough&&<Walkthrough onNav={nav} onDone={()=>setShowWalkthrough(false)} isMobile={Capacitor.isNativePlatform()}/>}
    </PeopleContext.Provider>
  );
}
